'use es6';

import { evaluateLinearEquation } from '../equation';
import { TOP, BOTTOM, LEFT, RIGHT } from '../constants';

// From https://stackoverflow.com/questions/2049582/how-to-determine-if-a-point-is-in-a-2d-triangle
const sign = (p1, p2, p3) => {
  return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
};
const isPositionInTriangle = (pt, v1, v2, v3) => {
  const b1 = sign(pt, v1, v2) < 0.0;
  const b2 = sign(pt, v2, v3) < 0.0;
  const b3 = sign(pt, v3, v1) < 0.0;
  return b1 === b2 && b2 === b3;
};
const calculateTrianglesFromBounds = boundsRect => {
  const topLeft = {
    x: boundsRect.left,
    y: boundsRect.top
  };
  const topRight = {
    x: boundsRect.right,
    y: boundsRect.top
  };
  const bottomLeft = {
    x: boundsRect.left,
    y: boundsRect.bottom
  };
  const bottomRight = {
    x: boundsRect.right,
    y: boundsRect.bottom
  };
  const middle = {
    x: boundsRect.left + boundsRect.width / 2,
    y: boundsRect.top + boundsRect.height / 2
  };
  return {
    [TOP]: [topLeft, topRight, middle],
    [LEFT]: [topLeft, middle, bottomLeft],
    [BOTTOM]: [bottomLeft, middle, bottomRight],
    [RIGHT]: [topRight, bottomRight, middle]
  };
};

// Assumes that we already know position is within boundsRects
export const determineLeftOrRightHalfPositionWithinBounds = (position, boundsRect) => {
  const middleX = boundsRect.left + boundsRect.width / 2;
  if (position.x < middleX) {
    return LEFT;
  }
  return RIGHT;
};

// Assumes that we already know position is within boundsRects
export const determinePositionTriangleWithinBounds = (position, boundsRect) => {
  const trianglesToCheck = calculateTrianglesFromBounds(boundsRect);
  for (const trianglePosition of Object.keys(trianglesToCheck)) {
    const triangleCoords = trianglesToCheck[trianglePosition];
    if (isPositionInTriangle(position, ...triangleCoords)) {
      return trianglePosition;
    }
  }
  return undefined;
};

// Assumes that we already know position is within boundsRect
export const determineTopOrBottomHalfPositionWithinBounds = (position, boundsRect) => {
  const middleY = boundsRect.top + boundsRect.height / 2;
  if (position.y < middleY) {
    return TOP;
  }
  return BOTTOM;
};
export const isCloseToLeftOrRightEdge = (epsilon, leftOrRight, position, boundsRect) => {
  if (leftOrRight === LEFT) {
    return position.x - boundsRect.left < epsilon;
  } else if (leftOrRight === RIGHT) {
    return boundsRect.right - position.x < epsilon;
  }
  throw new Error('Unknown or undefined leftOrRight identifier passed to isTrianglePositionCloseToEdgeOfBounds');
};
export const isCloseToTopOrBottomEdge = (epsilon, topOrBottom, position, boundsRect) => {
  if (topOrBottom === TOP) {
    return position.y - boundsRect.top < epsilon;
  } else if (topOrBottom === BOTTOM) {
    return boundsRect.bottom - position.y < epsilon;
  }
  throw new Error('Unknown or undefined topOrBottom identifier passed to isTrianglePositionCloseToEdgeOfBounds');
};
export const isTrianglePositionCloseToEdgeOfBounds = (epsilon, triangleKey, position, boundsRect) => {
  if (triangleKey === LEFT) {
    return position.x - boundsRect.left < epsilon;
  } else if (triangleKey === TOP) {
    return position.y - boundsRect.top < epsilon;
  } else if (triangleKey === BOTTOM) {
    return boundsRect.bottom - position.y < epsilon;
  } else if (triangleKey === RIGHT) {
    return boundsRect.right - position.x < epsilon;
  }
  throw new Error('Unknown or undefined triangleKey passed to isTrianglePositionCloseToEdgeOfBounds');
};
export const horizontalAndVericalCloseToEdgeDistanceForThisNode = ({
  width,
  height,
  isLeaf,
  closeToEdgeDistances,
  indexValues
}) => {
  const equationVariables = {
    width,
    height
  };
  let verticalCloseToEdge;
  let horizontalCloseToEdge;
  if (isLeaf) {
    verticalCloseToEdge = evaluateLinearEquation(closeToEdgeDistances.VERTICAL_GROUPING_EQUATION_FOR_LEAF, equationVariables, indexValues);
  } else {
    verticalCloseToEdge = evaluateLinearEquation(closeToEdgeDistances.VERTICAL_GROUPING_EQUATION_FOR_INNER, equationVariables, indexValues);
  }
  if (isLeaf) {
    horizontalCloseToEdge = evaluateLinearEquation(closeToEdgeDistances.HORIZONTAL_GROUPING_EQUATION_FOR_LEAF, equationVariables, indexValues);
  } else {
    horizontalCloseToEdge = evaluateLinearEquation(closeToEdgeDistances.HORIZONTAL_GROUPING_EQUATION_FOR_INNER, equationVariables, indexValues);
  }
  return {
    verticalCloseToEdge,
    horizontalCloseToEdge
  };
};
export const closeToEdgeDistancePerTriangle = ({
  triangleHovered,
  verticalCloseToEdge,
  horizontalCloseToEdge
}) => {
  if (triangleHovered === TOP || triangleHovered === BOTTOM) {
    return verticalCloseToEdge;
  } else if (triangleHovered === LEFT || triangleHovered === RIGHT) {
    return horizontalCloseToEdge;
  }
  throw new Error('Not supposed to get here');
};